import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import { format } from "date-fns";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import LawGrievanceAPI from "../../utils/api/LawGrievanceAPI";
import { notify_error } from "../Notify";
import "./law.css";

const AVATAR_COLORS = [
  "#6366f1", "#0ea5e9", "#10b981", "#f59e0b",
  "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6",
];
const avatarColor = (str = "") => {
  let sum = 0;
  for (let i = 0; i < str.length; i++) sum += str.charCodeAt(i);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
};
const initials = (name = "") => {
  const parts = name.trim().split(/\s+/);
  if (!parts[0]) return "?";
  return ((parts[0][0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

const Field = ({ label, value }) => (
  <Col md={6}>
    <div className="law-field">
      <div className="law-field__label">{label}</div>
      <div className="law-field__value">
        {value || <span className="law-muted">—</span>}
      </div>
    </div>
  </Col>
);

const LawGrievanceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Tracks which document action is running, to disable its button.
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    LawGrievanceAPI.findOne(id)
      .then((res) => {
        if (res.data.status) {
          setData(res.data.data);
        } else {
          notify_error(res.data.message || "Grievance not found");
        }
        setLoading(false);
      })
      .catch((err) => {
        notify_error(err.message || "Something went wrong");
        setLoading(false);
      });
  }, [id]);

  const fileNameFromKey = (key) => String(key).split("/").pop() || key;

  const openDocument = async (key) => {
    try {
      setBusyKey(key + ":view");
      const res = await LawGrievanceAPI.getDocumentUrl(key);
      if (res.data.status && res.data.url) {
        window.open(res.data.url, "_blank", "noopener,noreferrer");
      } else {
        notify_error(res.data.message || "Unable to open document");
      }
    } catch (err) {
      notify_error(err.message || "Unable to open document");
    } finally {
      setBusyKey(null);
    }
  };

  const downloadDocument = async (key) => {
    try {
      setBusyKey(key + ":download");
      const res = await LawGrievanceAPI.getDocumentUrl(key);
      if (res.data.status && res.data.url) {
        // Fetch the file and force a download so the browser saves it
        // instead of navigating to the presigned URL.
        const fileRes = await fetch(res.data.url);
        const blob = await fileRes.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileNameFromKey(key);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      } else {
        notify_error(res.data.message || "Unable to download document");
      }
    } catch (err) {
      notify_error(err.message || "Unable to download document");
    } finally {
      setBusyKey(null);
    }
  };

  const documents = Array.isArray(data?.document) ? data.document : [];

  return (
    <Container fluid className="law-page custom-sidebar">
      <div className="law-shell">
        {/* Top bar */}
        <div
          className="d-flex align-items-center gap-2"
          style={{ margin: "16px 0" }}
        >
          <button
            className="law-back-btn"
            onClick={() => navigate("/dashboard/law-grievance")}
          >
            <ArrowBackIcon fontSize="small" />
            Back
          </button>
        </div>

        {loading ? (
          <div className="law-loading">Loading grievance details…</div>
        ) : !data ? (
          <div className="law-loading">No record found.</div>
        ) : (
          <>
            {/* Hero */}
            <div className="law-detail-hero">
              <span
                className="law-avatar"
                style={{ background: avatarColor(data.student_name) }}
              >
                {initials(data.student_name)}
              </span>
              <div>
                <h2 className="law-detail-hero__name">{data.student_name}</h2>
                <div className="law-detail-hero__meta">
                  <span>
                    <EmailOutlinedIcon style={{ fontSize: "16px" }} />
                    {data.email}
                  </span>
                  <span>
                    <SchoolOutlinedIcon style={{ fontSize: "16px" }} />
                    {data.program} · Sem {data.semester}
                  </span>
                  {data.created_at && (
                    <span>
                      Submitted{" "}
                      {format(new Date(data.created_at), "dd MMM yyyy, hh:mm a")}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Applicant details */}
            <div className="law-section">
              <div className="law-section__title">
                <PersonOutlineIcon style={{ fontSize: "18px" }} />
                Applicant Details
              </div>
              <Row>
                <Field label="Student Name" value={data.student_name} />
                <Field label="Email ID" value={data.email} />
                <Field label="Father's Name" value={data.father_name} />
                <Field label="Mother's Name" value={data.mother_name} />
                <Field label="Mobile" value={data.mobile} />
                <Field label="Session" value={data.session} />
                <Field label="Program" value={data.program} />
                <Field label="Semester" value={data.semester} />
                <Field label="Problem Type" value={data.problem_type} />
                <Field
                  label="Submitted On"
                  value={
                    data.created_at
                      ? format(new Date(data.created_at), "dd/MM/yyyy hh:mm a")
                      : ""
                  }
                />
              </Row>
            </div>

            {/* Problem description */}
            <div className="law-section">
              <div className="law-section__title">
                <DescriptionOutlinedIcon style={{ fontSize: "18px" }} />
                Problem Description
              </div>
              {data.problem_description ? (
                <div className="law-description">{data.problem_description}</div>
              ) : (
                <span className="law-muted">No description provided.</span>
              )}
            </div>

            {/* Documents */}
            <div className="law-section">
              <div className="law-section__title">
                <AttachFileIcon style={{ fontSize: "18px" }} />
                Uploaded Document{documents.length > 1 ? "s" : ""}
              </div>
              {documents.length === 0 ? (
                <span className="law-muted">No document uploaded.</span>
              ) : (
                documents.map((key, i) => (
                  <div className="law-doc-row" key={i}>
                    <div className="law-doc-info">
                      <span className="law-doc-thumb">
                        <InsertDriveFileOutlinedIcon />
                      </span>
                      <span className="law-doc-name" title={fileNameFromKey(key)}>
                        {fileNameFromKey(key)}
                      </span>
                    </div>
                    <div className="law-doc-actions">
                      <button
                        className="law-btn law-btn--ghost"
                        onClick={() => openDocument(key)}
                        disabled={busyKey === key + ":view"}
                      >
                        <VisibilityOutlinedIcon style={{ fontSize: "16px" }} />
                        {busyKey === key + ":view" ? "Opening…" : "View"}
                      </button>
                      <button
                        className="law-btn law-btn--primary"
                        onClick={() => downloadDocument(key)}
                        disabled={busyKey === key + ":download"}
                      >
                        <DownloadOutlinedIcon style={{ fontSize: "16px" }} />
                        {busyKey === key + ":download" ? "Downloading…" : "Download"}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </Container>
  );
};

export default LawGrievanceDetail;
